import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import _ from 'lodash';
import { forwardRef, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Comment } from '../../../../../interfaces/Comment';
import CommentCard from '../../../../common/comments/commentCard';


interface CommentsDrawerProps {
  enabledComments: boolean;
  comments: Comment[];
  activeCommentID: string | null;
  searchQuery: string;
  mentionsMode: string;
  isLoading: boolean;
  setMentionsMode: React.Dispatch<React.SetStateAction<string>>;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setCommentState: React.Dispatch<React.SetStateAction<boolean>>;
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  deleteComment: (id: string) => void;
  resolveComment: (id: string) => void;
  fetchSearchedComments: (
    query: string,
    mentionsOnly: string,
    oldCommentList: Comment[],
  ) => void;
}

const CommentsDrawer = forwardRef<HTMLDivElement, CommentsDrawerProps>(
  function CommentsDrawer(props, ref) {
    const {
      enabledComments,
      comments,
      activeCommentID,
      searchQuery,
      mentionsMode,
      isLoading,
      setMentionsMode,
      setSearchQuery,
      setCommentState,
      setComments,
      deleteComment,
      resolveComment,
      fetchSearchedComments,
    } = props;

    const DEBOUNCE_DURATION_MS = 450;

    const drawerStyle: SxProps<Theme> = {
      width: '338px',
      padding: '20px',
      '& .MuiDrawer-paper': {
        width: '338px',
        boxSizing: 'border-box',
      },
    };

    const buttonStyle: SxProps<Theme> = {
      textTransform: 'none',
    };

    const closeComments = () => {
      setCommentState(false);
    };

    const searchComments = useRef(
      _.debounce((query: string, mustMention: string, list: Comment[]) => {
        fetchSearchedComments(query, mustMention, list);
      }, DEBOUNCE_DURATION_MS),
    ).current;

    const handleSearchInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setSearchQuery(event.target.value);
      searchComments(event.target.value, mentionsMode, comments);
    };

    const handleMentionSelectChange = (event: SelectChangeEvent<string>) => {
      setMentionsMode(event.target.value);
      searchComments(searchQuery, event.target.value, comments);
    };

    const handleRefreshClicked = () => {
      fetchSearchedComments(searchQuery, mentionsMode, comments);
    };

    useEffect(() => {
      return () => {
        searchComments.cancel();
      };
    }, [searchComments]);

    return (
      <Drawer
        variant='persistent'
        anchor='right'
        open={enabledComments}
        sx={drawerStyle}
      >
        <Stack
          direction='row'
          justifyContent='flex-end'
          spacing={1}
          padding={1}
        >
          <Button
            color='neutralGray'
            startIcon={<RefreshIcon />}
            sx={buttonStyle}
            onClick={handleRefreshClicked}
          >
            <FormattedMessage defaultMessage='Refresh page' />
          </Button>
          <Button
            color='neutralGray'
            startIcon={<CloseIcon />}
            onClick={closeComments}
            sx={buttonStyle}
          >
            <FormattedMessage defaultMessage='Close' />
          </Button>
        </Stack>
        <Box marginX='16px' marginBottom='8px'>
          <Typography fontSize={14} fontWeight={500}>
            <FormattedMessage defaultMessage='Comments' />
          </Typography>
          <Stack direction='row' spacing={1} marginTop='12px'>
            <TextField
              variant='standard'
              hiddenLabel
              value={searchQuery}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder='Search comments'
              onChange={handleSearchInputChange}
            />
            <FormControl
              sx={{
                minWidth: '130px',
              }}
            >
              <Select
                variant='standard'
                disableUnderline
                value={`${mentionsMode}`}
                IconComponent={KeyboardArrowDownIcon}
                onChange={handleMentionSelectChange}
              >
                <MenuItem value='False'>
                  <FormattedMessage defaultMessage='All comments' />
                </MenuItem>
                <MenuItem value='True'>
                  <FormattedMessage defaultMessage='Mentions' />
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
        <Divider />
        <div
          ref={ref}
          style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '10px',
            padding: '16px',
            height: '80%',
            overflowY: 'auto',
          }}
        >
          {isLoading ? (
            <Stack direction='row' justifyContent='center'>
              <CircularProgress />
            </Stack>
          ) : (
            comments.map((comment, index) => (
              <div id={`comment-${comment.id}`} key={index}>
                <CommentCard
                  comment={comment}
                  isActive={comment.id === activeCommentID}
                  setComments={setComments}
                  deleteComment={deleteComment}
                  resolveComment={resolveComment}
                />
              </div>
            ))
          )}
        </div>
      </Drawer>
    );
  },
);

export default CommentsDrawer;
